import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import Tabs from '@atlaskit/tabs/tabs';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { QueryIntent } from '../../constants';

import { messages } from './messages';
import { normalizeDebuggerData } from './normalize-debugger-data';
import { DebuggerTabList } from './tab-list';
import { ElsOverviewPanel } from './tab-panels/els-overview-panel';
import { LoopPanel } from './tab-panels/loop-panel';
import { OrchestrationOverviewPanel } from './tab-panels/orchestration-overview-panel';
import { PluginInvocationsPanel } from './tab-panels/plugin-invocations-panel';
import { RankedSearchResultsPanel } from './tab-panels/ranked-search-results-panel';
import { RawJsonPanel } from './tab-panels/raw-json-panel';
import { RawSearchResultsPanel } from './tab-panels/raw-search-results-panel';
import { RequestOverviewPanel } from './tab-panels/request-overview-panel';

export type OnlineDebuggerModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

export function OnlineDebuggerModal({
	isOpen,
	onClose,
}: OnlineDebuggerModalProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const {
		commonAttributes,
		smartAnswersRoute,
		queryIntentClassificationData,
		queryIntentDetectionMetadata,
		queryIntentDetectionTraceId,
	} = useAIAnswerContext();
	const isNaturalLanguageQuery = smartAnswersRoute === QueryIntent.NATURAL_LANGUAGE_QUERY;

	const { extraAttributes, sources } = commonAttributes;
	const debuggerData = useMemo(
		() =>
			normalizeDebuggerData(
				extraAttributes,
				fg('kd_consolidate_sources_analytics') ? sources : undefined,
				fg('kd_fe_persons_intent_detection_els')
					? {
							intentData: queryIntentClassificationData,
							detectionMetadata: queryIntentDetectionMetadata,
							intentDetectionTraceId: queryIntentDetectionTraceId,
						}
					: undefined,
			),
		[
			extraAttributes,
			sources,
			queryIntentClassificationData,
			queryIntentDetectionMetadata,
			queryIntentDetectionTraceId,
		],
	);

	const showPluginsPanels =
		isNaturalLanguageQuery &&
		!debuggerData.minions.hasMinionOutputs &&
		debuggerData.pluginInvocations.hasPluginInvocations;

	return (
		<ModalTransition>
			{isOpen && (
				<Modal onClose={onClose} width="x-large" testId="sain-online-debugger-modal">
					<ModalHeader hasCloseButton>
						<ModalTitle>{formatMessage(messages.ai_debugger_modal_title)}</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<Tabs id="sain-online-debugger-tabs">
							<DebuggerTabList
								isNaturalLanguageQuery={isNaturalLanguageQuery}
								hasMinionOutputs={debuggerData.minions.hasMinionOutputs}
								hasPluginInvocations={debuggerData.pluginInvocations.hasPluginInvocations}
								hasElsData={debuggerData.elsOverview.hasElsData}
								roundCount={debuggerData.minions.roundCount}
							/>
							<RequestOverviewPanel
								{...debuggerData.requestOverview}
								isNaturalLanguageQuery={isNaturalLanguageQuery}
							/>
							{debuggerData.elsOverview.hasElsData && (
								<ElsOverviewPanel {...debuggerData.elsOverview} />
							)}
							{showPluginsPanels && (
								<RawSearchResultsPanel searchResults={debuggerData.rawSearch.searchResults} />
							)}
							{showPluginsPanels && (
								<RankedSearchResultsPanel
									rankedDocumentsWithChunks={debuggerData.rankedSearch.rankedDocumentsWithChunks}
								/>
							)}
							{showPluginsPanels && (
								<PluginInvocationsPanel
									nonSearchPluginInvocations={
										debuggerData.pluginInvocations.nonSearchPluginInvocations
									}
								/>
							)}
							{isNaturalLanguageQuery && (
								<OrchestrationOverviewPanel
									orchestrationRounds={debuggerData.minions.orchestrationRounds}
								/>
							)}
							{isNaturalLanguageQuery &&
								debuggerData.minions.hasMinionOutputs &&
								debuggerData.minions.orchestrationRounds.map((round, index) => (
									<LoopPanel key={`loop-panel-${index}`} round={round} roundIndex={index} />
								))}
							{isNaturalLanguageQuery && <RawJsonPanel rawJson={debuggerData.rawJson.rawJson} />}
						</Tabs>
					</ModalBody>
					<ModalFooter />
				</Modal>
			)}
		</ModalTransition>
	);
}
